import React from "react";
import { analyticsHandler } from "./eventLogger.js";

const MODULE_NAME = "pg_patterns";

// Toggle Switch Component
function CompactToggleSwitch({ checked, onChange, disabled = false, componentName = "" }) {
    const handleToggle = (newValue) => {
        onChange(newValue);
    };

    const metaData = JSON.stringify({
        toggle_link_value: componentName,
        enabled: !checked ? 1 : 0
    });

    return (
        <span
            style={{
                position: "relative",
                display: "inline-block",
                width: 40,
                height: 20,
                cursor: disabled ? "not-allowed" : "pointer",
                opacity: disabled ? 0.5 : 1,
            }}
            onClick={analyticsHandler({
                event_type: "toggle_click",
                module_name: MODULE_NAME,
                subcomponent: "left-nav-toggle-click",
                screen: window.location.pathname,
            })}
            data-analytics={MODULE_NAME}
            data-analytics-meta={metaData}
        >
            <input
                type="checkbox"
                checked={checked}
                onChange={() => !disabled && handleToggle(!checked)}
                style={{ opacity: 0, width: 0, height: 0 }}
                disabled={disabled}
            />
            <span
                style={{
                    position: "absolute",
                    cursor: disabled ? "not-allowed" : "pointer",
                    top: 0, left: 0, right: 0, bottom: 0,
                    background: checked ? "#e0b400" : "#555",
                    borderRadius: 20,
                    transition: "background 0.25s"
                }}
            />
            <span
                style={{
                    position: "absolute",
                    left: checked ? 18 : 2,
                    top: 2,
                    width: 16,
                    height: 16,
                    background: "#fff",
                    borderRadius: "50%",
                    transition: "left 0.25s",
                    boxShadow: "0 2px 4px rgba(0,0,0,0.2)"
                }}
            />
        </span>
    );
}

const LeftNav_patterns = ({
    navExpanded,
    navPinned,
    setNavPinned,
    setNavHovered,
    navGroups,
    selected,
    setSelected,
    loadingSelectedPattern,
    updatingPattern,
    handlePatternToggle
}) => {
    return (                        
        <nav
            className={`side-nav${navExpanded ? " expanded" : " collapsed"}`}
            style={{
                width: navExpanded ? 280 : 64,
                minWidth: navExpanded ? 240 : 64,
                background: "#23263a",
                color: "#fff",
                transition: "width 0.3s ease, min-width 0.3s ease",
                overflowX: "hidden",
                overflowY: "auto",
                position: "relative",
                borderRight: "1px solid #222",
                zIndex: 2,
                boxSizing: "border-box",
                flexShrink: 0 // Prevent main content from squishing nav
            }}
            onMouseEnter={() => setNavHovered(true)}
            onMouseLeave={() => setNavHovered(false)}
        >
            {/* Pin/Unpin Icon Container */}
            <div
                style={{
                    position: "absolute",
                    top: 10,
                    right: navExpanded ? 10 : 18,
                    background: "#23263a",
                    borderRadius: "50%",
                    width: 28,
                    height: 28,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    boxShadow: navExpanded ? "0 2px 8px #0002" : "none",
                    cursor: "pointer",
                    border: "1px solid #444",
                    zIndex: 3,
                    transition: "right 0.3s ease",
                }}
                title={navPinned ? "Unpin navigation" : "Pin navigation"}
                onClick={(e) => {
                    e.stopPropagation();
                    setNavPinned(!navPinned);
                }}
            >
                {navPinned ? (
                    <span style={{ fontSize: 18 }}>📌</span>
                ) : (
                    <span style={{ fontSize: 18, transform: "rotate(-30deg)" }}>📍</span>
                )}
            </div>

            <div style={{ marginTop: 48, width: 280 }}> {/* Fixed inner width to prevent layout jump */}
                {navGroups.map((group) => (
                    <div key={group.label} style={{ marginBottom: 18 }}>
                        <div
                            style={{
                                fontWeight: 700,
                                fontSize: 14,
                                color: "#e0b400",
                                padding: "8px 18px 4px 18px",
                                letterSpacing: 0.5,
                                whiteSpace: "nowrap",
                                transition: "opacity 0.2s",
                                opacity: navExpanded ? 1 : 0
                            }}
                        >
                            {navExpanded ? (
                                <>
                                    <span style={{ marginRight: 6 }}>▶</span>
                                    {group.label}
                                </>
                            ) : null}
                        </div>
                        {group.patterns.map((pat) => {
                            const isUpdating = updatingPattern === pat.pattern_id;
                            const isDisabled = !pat.enabled;
                            const isLoading = loadingSelectedPattern && selected === pat.key;

                            return (
                                <div
                                    key={pat.key}
                                    style={{
                                        display: "flex",
                                        alignItems: "center",
                                        padding: "10px 18px",
                                        cursor: isDisabled ? "default" : "pointer",
                                        background: selected === pat.key && !isDisabled ? "#1976d2" : "transparent",
                                        color: isDisabled ? "#666" : (selected === pat.key ? "#fff" : "#ccc"),
                                        fontWeight: selected === pat.key && !isDisabled ? 600 : 400,
                                        borderLeft: selected === pat.key && !isDisabled ? "4px solid #e0b400" : "4px solid transparent",
                                        opacity: isDisabled ? 0.6 : 1,
                                        transition: "background 0.15s, color 0.15s",
                                        gap: 12,
                                        width: "100%",
                                        boxSizing: "border-box"
                                    }}
                                    onClick={() => !isDisabled && setSelected(pat.key)}
                                >
                                    {/* Icon/First Letter - Always centered in the 64px width */}
                                    <div 
                                        style={{ 
                                            minWidth: 28, 
                                            textAlign: "center", 
                                            fontWeight: "bold", 
                                            fontSize: "1.1em",
                                            display: "flex",
                                            justifyContent: "center"
                                        }}
                                    >
                                        {pat.label[0]}
                                    </div>

                                    {/* Content - Fades out during collapse */}
                                    <div
                                        style={{
                                            flex: 1,
                                            whiteSpace: "nowrap",
                                            overflow: "hidden",
                                            textOverflow: "ellipsis",
                                            fontSize: "0.95em",
                                            transition: "opacity 0.2s",
                                            opacity: navExpanded ? 1 : 0,
                                            display: "flex",
                                            alignItems: "center",
                                            gap: 8
                                        }}
                                    >
                                        {pat.label}
                                        {isLoading && <span style={{ fontSize: "0.8em" }}>⏳</span>}
                                    </div>

                                    {navExpanded && (
                                        <div 
                                            onClick={(e) => e.stopPropagation()}
                                            style={{ opacity: navExpanded ? 1 : 0, transition: "opacity 0.2s" }}
                                        >
                                            <CompactToggleSwitch
                                                checked={pat.enabled}
                                                onChange={() => handlePatternToggle(pat.pattern_id, !pat.enabled)}
                                                disabled={isUpdating}
                                                componentName={pat.component_name}
                                            />
                                        </div>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                ))}
            </div>
        </nav>
    );
};

export default LeftNav_patterns;